import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { ContactFields, EMPTY_CONTACT } from "./ContactFields";

// Public page guests reach by scanning the event QR code.
export default function EventSignupPage() {
  const { slug } = useParams();
  const [event, setEvent] = useState(undefined);
  const [form, setForm] = useState(EMPTY_CONTACT);
  const [website, setWebsite] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/public/events/${slug}`).then((r) => setEvent(r.data)).catch(() => setEvent(null));
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim() && !form.phone.trim()) {
      toast.error("Please add an email or phone number.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/public/events/${slug}/signup`, { ...form, email: form.email.trim() || null, website });
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't sign you up. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (event === undefined) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand-teal" /></div>;
  }
  return (
    <div className="min-h-screen bg-brand-charcoal text-white" data-testid="event-signup-page">
      <div className="max-w-lg mx-auto px-5 pt-10 pb-16">
        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-brand-gold">Fresh Fast Foods</p>
        {!event ? (
          <h1 data-testid="event-signup-missing" className="mt-3 font-display font-black text-3xl">This sign-up link isn&apos;t valid.</h1>
        ) : done ? (
          <div data-testid="event-signup-thanks" className="mt-14 flex flex-col items-center text-center gap-4">
            <CheckCircle2 className="h-16 w-16 text-brand-gold" />
            <p className="font-display font-black text-3xl">You&apos;re on the list!</p>
            <p className="text-white/70">Thanks for stopping by {event.name}. We&apos;ll be in touch soon.</p>
            <a href="/menu" className="mt-4 h-12 px-8 inline-flex items-center rounded-full bg-white text-brand-charcoal font-bold uppercase tracking-[0.1em] text-[13px]">See our menu</a>
          </div>
        ) : !event.signup_open ? (
          <h1 data-testid="event-signup-closed" className="mt-3 font-display font-black text-3xl">Sign-ups for {event.name} are closed.</h1>
        ) : (
          <>
            <h1 className="mt-3 font-display font-black text-4xl leading-tight">Stay in touch</h1>
            <p className="mt-2 text-white/70 text-[15px]">{event.name} — leave your details and we&apos;ll follow up about catering.</p>
            <form onSubmit={submit} className="mt-8 rounded-3xl bg-white text-brand-charcoal p-5" data-testid="event-signup-form">
              <ContactFields value={form} onChange={setForm} testidPrefix="event-signup" required={["name"]} large />
              <input type="text" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} className="hidden" aria-hidden="true" />
              <button type="submit" disabled={busy} data-testid="event-signup-submit" className="mt-5 w-full h-14 rounded-full bg-brand-teal text-white font-bold uppercase tracking-[0.12em] text-[14px] inline-flex items-center justify-center gap-2 disabled:opacity-60">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Sign me up
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
